/**
 * Field catalog + event vocabulary + draft validation for the matter controls
 * on `/inbox/threads/:id`.
 *
 * The pure half of slice `inbox-matter-lifecycle`: which matter fields staff
 * may edit inline, which `record_inbox_event` kinds the UI offers, and the two
 * conditional-requirement rules the JSON Schema expresses as `allOf`/`if`/`then`
 * (which the schema-form compiler drops, so the modal must enforce them or the
 * operator meets a raw `input_invalid`).
 *
 * Reuses `PrEditFieldSpec` + `parsePrEditDraft` from `pr-person-edit.ts` — the
 * spec shape is entity-agnostic; only the catalog differs.
 *
 * WIRE RULES THIS MODULE ENCODES (all verified against
 * `packages/canonical-actions/src/actions/update-inbox-thread.ts` and
 * `record-inbox-event.ts`, not guessed):
 *
 *  - `update_inbox_thread` clears with an explicit `null`. Sending `''` is
 *    REJECTED (`input_invalid`) — the prose fields carry `minLength: 1` and
 *    `urgency` is an enum. Hence every clearable spec here sets
 *    `clearTo: "null"`.
 *  - `stage` is NOT in this catalog. `update_inbox_thread` admits only
 *    `closed | needs_mario`, and both are consequential enough to deserve a
 *    modal that collects a reason — see the header action row in
 *    `InboxThreadDetail.tsx`.
 *  - `next_action_at` is NOT in this catalog. It is a datetime, and an inline
 *    text editor that sends `null` for a blank draft would CLEAR the agent's
 *    due cursor — after which the matter is never swept again and stalls
 *    silently until `reconcile_inbox_board`'s 48h drift report.
 */

import type { PrEditFieldSpec } from "./pr-person-edit";

/** Matter fields editable inline, in render order. */
export const INBOX_MATTER_EDITABLE_FIELDS: ReadonlyArray<PrEditFieldSpec> = [
  {
    field: "category",
    label: "Category",
    kind: "text",
    clearable: true,
    clearTo: "null",
    placeholder: "e.g. supplier · landlord · press",
  },
  {
    field: "urgency",
    label: "Urgency",
    kind: "select",
    clearable: true,
    clearTo: "null",
    options: ["low", "normal", "high"],
  },
  {
    field: "summary",
    label: "Summary",
    kind: "multiline",
    clearable: true,
    clearTo: "null",
  },
  {
    field: "classifier_rationale",
    label: "Why this matters",
    kind: "multiline",
    clearable: true,
    clearTo: "null",
  },
  {
    field: "next_action",
    label: "Next action",
    kind: "multiline",
    clearable: true,
    clearTo: "null",
  },
];

/**
 * `record_inbox_event` kinds the UI offers.
 *
 * Deliberately NOT the full recordable set:
 *  - `feedback` is excluded server-side already (only `record_inbox_feedback`
 *    may write it, so an agent cannot launder a rejection).
 *  - `commitment_done` is excluded HERE because it requires a
 *    `resolves_timeline_id` that only the commitment row knows — it gets its
 *    own per-commitment button with the id prefilled.
 *  - `closed` is excluded because closing is its own control, with the
 *    `closed_reason` the schema requires.
 */
export const INBOX_LOG_EVENT_KINDS: ReadonlyArray<string> = [
  "opened",
  "received",
  "classified",
  "notified",
  "draft_staged",
  "reply_sent",
  "commitment",
  "escalated",
  "note",
];

/** The three urgency values `update_inbox_thread` admits. */
export const INBOX_URGENCY_OPTIONS: ReadonlyArray<string> = [
  "low",
  "normal",
  "high",
];

/**
 * Enforce `record_inbox_event`'s two conditional requirements client-side, so
 * the operator gets a sentence instead of a raw Ajv `input_invalid`.
 *
 * Returns `null` when the draft is submittable.
 */
export function validateInboxEventDraft(
  values: Readonly<Record<string, string | boolean>>,
): string | null {
  const kind = typeof values["kind"] === "string" ? values["kind"] : "";
  const stage = typeof values["stage"] === "string" ? values["stage"] : "";
  const closedReason =
    typeof values["closed_reason"] === "string" ? values["closed_reason"] : "";
  const resolves =
    typeof values["resolves_timeline_id"] === "string"
      ? values["resolves_timeline_id"]
      : "";

  if (stage === "closed" && closedReason.trim() === "") {
    return "Closing a matter needs a reason — say why it's done.";
  }
  if (kind === "commitment_done" && resolves.trim() === "") {
    return "Marking a commitment done needs the commitment it resolves.";
  }
  if (resolves.trim() !== "" && kind !== "commitment_done") {
    return "Only a 'commitment_done' event can resolve a commitment.";
  }
  return null;
}
